import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  Autocomplete,
  Button,
  Divider,
  Grid,
  Paper,
  TextField,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Stack,
  Tooltip,
  Card,
  CardContent,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  CircularProgress,
} from "@mui/material";
import { RemoveCircleOutline, Search } from "@mui/icons-material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import useFetchObjects from "../../../../api/useFetchObjects";
import useAdd from "../../../../api/useAdd";
import { useAuth } from "../../../../context";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { getLocalDateString } from "../../../../utils";
import PrescriptionPrint from "./PrescriptionPrint";

const getCurrentDate = () => new Date().toISOString().split("T")[0];
const getCurrentTime = () =>
  new Date().toTimeString().split(":").slice(0, 2).join(":");

// Debounce function
function debounce(func, delay) {
  let timeout;
  return function (...args) {
    const context = this;
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(context, args), delay);
  };
}

const AddPrescription = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;
  const navigate = useNavigate();
  const today = getLocalDateString();
  const [extraMedicines, setExtraMedicines] = useState([]);
  const [printData, setPrintData] = useState(null);
  const printRef = useRef(null);

  // Ticket-based selection
  const [ticketSearchTerm, setTicketSearchTerm] = useState("");
  const [ticketInputValue, setTicketInputValue] = useState("");
  const [forceUpdate, setForceUpdate] = useState(0);
  const [selectedTicketSnapshot, setSelectedTicketSnapshot] = useState(null);

  const [input, setInput] = useState({
    date: getCurrentDate(),
    time: getCurrentTime(),
    doctor: "",
    patient: "",
    ticket: "",
    duration: "",
    bp: "",
    pr: "",
    rr: "",
    diagnosis: "",
    discount: 0,
    total_price: 0,
    items: [],
  });
  const [lastAddedIndex, setLastAddedIndex] = useState(null);

  const { data: doctors } = useFetchObjects(["doctors"], "users", token);
  const { data: medicines } = useFetchObjects(
    ["stock-medicines"],
    `stock-medicines/`,
    token
  );

  // Pagination and Search State for Patients
  const [patientPage, setPatientPage] = useState(1);
  const [patientPageSize] = useState(100);
  const [patientSearchTerm, setPatientSearchTerm] = useState("");
  const [allPatients, setAllPatients] = useState([]);
  const [patientHasMore, setPatientHasMore] = useState(true);
  const [patientInputValue, setPatientInputValue] = useState("");
  const [patientForceUpdate, setPatientForceUpdate] = useState(0);

  // Fetch tickets
  const effectiveSearch = input.ticket ? "" : ticketSearchTerm;
  const {
    data: ticketsData,
    isLoading: isLoadingTickets,
  } = useFetchObjects(
    ["tickets-pharmacy", today, effectiveSearch, forceUpdate],
    `tickets/?status=1&visit_date=${today}&search=${encodeURIComponent(effectiveSearch)}&page_size=200`,
    token
  );

  const tickets = ticketsData?.results || ticketsData || [];
  const ticketsList = Array.isArray(tickets) ? tickets : [];

  const {
    data: patientsData,
    isLoading: isLoadingPatients,
    refetch: refetchPatients,
  } = useFetchObjects(
    [
      "patients",
      patientPage,
      patientPageSize,
      patientSearchTerm,
      patientForceUpdate,
    ],
    `patients/?page=${patientPage}&page_size=${patientPageSize}&search=${patientSearchTerm}`,
    token
  );

  const [formErrors, setFormErrors] = useState({});
  const [autocompleteInputValue, setAutocompleteInputValue] = useState("");
  const autocompleteInputRef = useRef(null);
  const quantityRefs = useRef([]);
  const priceRefs = useRef([]);
  const notationRefs = useRef([]);
  const isBeforeRefs = useRef([]);
  const unitsPerMealRefs = useRef([]);
  const timesPer24Refs = useRef([]);

  const {
    handleAdd: handleAddPrescription,
    resetForm,
    loading,
    responseData,
  } = useAdd(
    "prescriptions",
    token,
    t("pharmacy.prescriptionCreatedSuccess"),
    t("pharmacy.prescriptionCreateFail")
  );

  const handlePrintPrescription = useCallback(() => {
    if (!printRef.current) return;

    const printWindow = window.open("", "_blank");
    if (!printWindow) return;

    printWindow.document.write(`
      <html>
        <head>
          <title>Prescription Print</title>
          <style>
            @page {
              size: A4 portrait;
              margin: 0;
            }
            html, body {
              margin: 0;
              padding: 0;
              width: 210mm;
              height: 297mm;
              overflow: hidden;
              background: #fff;
            }
            body {
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            body > div {
              width: 210mm !important;
              height: 297mm !important;
              overflow: hidden !important;
              page-break-after: avoid !important;
              break-after: avoid-page !important;
            }
          </style>
        </head>
        <body>
          ${printRef.current.innerHTML}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  }, []);

  useEffect(() => {
    if (resetForm) {
      setInput({
        date: getCurrentDate(),
        time: getCurrentTime(),
        doctor: "",
        patient: "",
        ticket: "",
        duration: "",
        bp: "",
        pr: "",
        rr: "",
        diagnosis: "",
        discount: 0,
        total_price: 0,
        items: [],
      });
      setFormErrors({});
      setAllPatients([]);
      setPatientPage(1);
      setPatientHasMore(true);
      setPatientInputValue("");
      setTicketInputValue("");
      setSelectedTicketSnapshot(null);
      setExtraMedicines([]);
    }
  }, [resetForm]);

  useEffect(() => {
    if (!responseData) return;

    setPrintData(responseData);
  }, [responseData]);

  useEffect(() => {
    if (!printData) return;

    const timer = setTimeout(() => {
      handlePrintPrescription();
    }, 500);

    return () => clearTimeout(timer);
  }, [printData, handlePrintPrescription]);

  useEffect(() => {
    setInput((prev) => ({ ...prev, extra_medicines: extraMedicines }));
  }, [extraMedicines]);

  useEffect(() => {
    if (patientsData?.results) {
      setAllPatients((prevPatients) => {
        const newPatients = [...prevPatients, ...patientsData.results];
        const uniquePatients = newPatients.reduce((acc, current) => {
          if (!acc.find((item) => item.id === current.id)) {
            acc.push(current);
          }
          return acc;
        }, []);
        return uniquePatients;
      });
      setPatientHasMore(patientsData.next !== null);
    } else {
      setPatientHasMore(false);
    }
  }, [patientsData]);

  useEffect(() => {
    if (!patientSearchTerm) {
      setAllPatients([]);
      setPatientPage(1);
      setPatientHasMore(true);
      refetchPatients();
    }
  }, [patientSearchTerm, refetchPatients]);

  const handleChangePrescriptionField = (e) => {
    const { name, value } = e.target;
    setInput((prev) => ({ ...prev, [name]: value }));
  };

  // New handler to handle discount changes and recalculate total price accordingly
  const handleDiscountChange = (e) => {
    const discountValue = parseInt(e.target.value) || 0;
    const newTotalPrice = Math.max(
      calculateTotalPrice(input.items) - discountValue,
      0
    );
    setInput((prev) => ({
      ...prev,
      discount: discountValue,
      total_price: newTotalPrice,
    }));
  };

  const handleAddNewItem = (medicine) => {
    if (medicine) {
      setInput((prevInput) => {
        const newItems = [
          ...prevInput.items,
          {
            stock_medicine: medicine.id,
            name: medicine.medicine_display,
            quantity: 0,
            sell_price: medicine.sell_price,
            total_price: 0,
            available_stock: medicine.quantity,
            notation: "",
            is_before: "after",
            units_per_meal: 0,
            times_per_24: 0,
          },
        ];
        const total = Math.max(
          calculateTotalPrice(newItems) - (prevInput.discount || 0),
          0
        );
        const newIndex = newItems.length - 1;
        setTimeout(() => {
          quantityRefs.current[newIndex]?.focus();
        }, 50);
        return {
          ...prevInput,
          items: newItems,
          total_price: total,
        };
      });
      setAutocompleteInputValue("");
    }
  };


  const handleChangeItemField = (index, e) => {
    const { name, value } = e.target;
    const items = [...input.items];
    items[index][name] = value ? parseInt(value) || value : "";
    if (name === "quantity" || name === "sell_price") {
      items[index].total_price =
        items[index].sell_price * items[index].quantity;
    }
    // Recalculate total price minus discount
    const total = Math.max(
      calculateTotalPrice(items) - (input.discount || 0),
      0
    );
    setInput((prev) => ({ ...prev, items, total_price: total }));
  };

  const handleKeyDown = (e, index, field) => {
    if (e.key === "Enter" || e.key === "Tab") {
      e.preventDefault();
      if (field === "quantity") {
        unitsPerMealRefs.current[index]?.focus();
      } else if (field === "units_per_meal") {
        timesPer24Refs.current[index]?.focus();
      } else if (field === "times_per_24") {
        notationRefs.current[index]?.focus();
      } else if (field === "notation") {
        if (quantityRefs.current[index + 1]) {
          quantityRefs.current[index + 1].focus();
        } else {
          autocompleteInputRef.current?.focus();
        }
      }
    }
  };

  const handleZeroReplaceFocus = (e) => {
    if (e.target.value === "0" || e.target.value === 0) {
      e.target.select();
    }
  };

  const handleZeroReplaceClick = (e) => {
    if (e.target.value === "0" || e.target.value === 0) {
      e.target.select();
    }
  };

  const addItems = () => {
    setExtraMedicines([
      ...extraMedicines,
      {
        title: "",
        dosage: "",
        quantity: "",
        notation: "",
        units_per_meal: "",
        times_per_24: "",
        is_before: "",
      },
    ]);
  };

  const removeItem = (index) => {
    setExtraMedicines(extraMedicines?.filter((_, i) => i !== index));
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...extraMedicines];
    newItems[index][field] = value;
    setExtraMedicines(newItems);
  };

  const handleRemoveItem = (index) => {
    const items = [...input.items];
    items.splice(index, 1);
    const total = Math.max(
      calculateTotalPrice(items) - (input.discount || 0),
      0
    );
    setInput((prev) => ({ ...prev, items, total_price: total }));
  };

  const calculateTotalPrice = (items) =>
    items.reduce(
      (sum, item) => sum + (item.sell_price * item.quantity || 0),
      0
    );

  const handleSave = (e) => {
    e.preventDefault();
    let isValid = true;
    let errors = {};

    if (!input.date) {
      errors.date = t("pharmacy.dateRequired");
      isValid = false;
    }
    if (!input.doctor) {
      errors.doctor = t("pharmacy.doctorRequired");
      isValid = false;
    }
    input.items.forEach((item, index) => {
      if (!item.stock_medicine) {
        errors[`stock_medicine_${index}`] = t("pharmacy.medicineRequired");
        isValid = false;
      }
      if (!item.quantity) {
        errors[`quantity_${index}`] = t("pharmacy.quantityRequired");
        isValid = false;
      }
      if (!item.sell_price) {
        errors[`sell_price_${index}`] = t("pharmacy.priceRequired");
        isValid = false;
      }
    });

    setFormErrors(errors);
    if (isValid) {
      // Send discount and total_price in data
      const dataToSend = {
        ...input,
        patient: input.patient || null,
        ticket: input.ticket || null,
        total_price: input.total_price,
        discount: input.discount,
      };
      handleAddPrescription(dataToSend);
      setInput((prevInput) => ({
        ...prevInput,
        patient: "",
        ticket: "",
      }));
      setTicketInputValue("");
    }
  };

  // Patient Autocomplete Handlers
  const handlePatientInputChange = (event, newInputValue) => {
    setPatientInputValue(newInputValue);
    debouncedSetPatientSearchTerm(newInputValue);
  };

  const handlePatientChange = (event, newValue) => {
    setInput((prev) => ({ ...prev, patient: newValue ? newValue.id : "" }));
  };

  const handlePatientScroll = (event) => {
    const { scrollTop, clientHeight, scrollHeight } = event.target;
    if (
      scrollHeight - scrollTop <= clientHeight + 50 &&
      patientHasMore &&
      !isLoadingPatients
    ) {
      setPatientPage((prevPage) => prevPage + 1);
    }
  };

  // Debounced search terms
  const debouncedSetPatientSearchTerm = useCallback(
    debounce((value) => {
      setPatientSearchTerm(value);
    }, 300),
    []
  );

  // Force update functions
  const forceUpdatePatientAutocomplete = () => {
    setPatientForceUpdate((prev) => prev + 1);
  };

  useEffect(() => {
    if (patientSearchTerm === "") {
      forceUpdatePatientAutocomplete();
    }
  }, [patientSearchTerm]);

  // Ticket handlers
  const handleTicketInputChange = (event, newInputValue) => {
    setTicketInputValue(newInputValue);
    debouncedSetTicketSearch(newInputValue);
  };

  const getTicketOptionLabel = (option) =>
    `${option.ticket_number || option.id} — ${option.patient_name_display || ""} ${option.patient_phone_display ? `(${option.patient_phone_display})` : ""}`;

  const handleTicketChange = (event, newValue) => {
    if (newValue) {
      setInput({
        ...input,
        ticket: newValue.id,
        patient: newValue.patient || "",
      });
      setTicketInputValue(getTicketOptionLabel(newValue));
      setTicketSearchTerm("");
      setSelectedTicketSnapshot(newValue);
    } else {
      setInput({ ...input, ticket: "", patient: "" });
      setTicketInputValue("");
      setTicketSearchTerm("");
      setSelectedTicketSnapshot(null);
    }
  };

  const debouncedSetTicketSearch = useCallback(
    debounce((value) => setTicketSearchTerm(value), 300),
    []
  );

  const selectedTicketFromList = ticketsList.find((t) => t.id === input.ticket);
  const selectedTicket =
    selectedTicketFromList ||
    (input.ticket && selectedTicketSnapshot?.id === input.ticket ? selectedTicketSnapshot : null);

  const ticketOptions =
    selectedTicket &&
    !ticketsList.some((t) => t.id === selectedTicket.id)
      ? [selectedTicket, ...ticketsList]
      : ticketsList;

  return (
    <>
      <Card
        sx={{
          boxShadow: "0px 20px 50px rgba(0, 0, 0, 0.1)",
          borderRadius: 4,
          backgroundColor: "#f4f6f8",
          width: "100%",
        }}
      >
        <CardContent sx={{ p: 4 }}>
        {/* Go Back Icon and Title */}
        <Box display="flex" alignItems="center" mb={4}>
          <Tooltip title={t("pharmacy.goBack")} arrow>
            <IconButton
              onClick={() => navigate(-1)}
              aria-label={t("pharmacy.goBack")}
              sx={{ mr: 2 }}
            >
              <ArrowBackIcon size="large" />
            </IconButton>
          </Tooltip>
        </Box>

        <Typography
          variant="h5"
          fontWeight={600}
          color="primary"
          textAlign="center"
          gutterBottom
        >
          {t("pharmacy.addPrescriptionTitle")}
        </Typography>

        <Divider sx={{ my: 3 }} />

        <Box mb={4}>
          <Typography variant="subtitle1" fontWeight={500} mb={2}>
            {t("pharmacy.patientInfo")}
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t("pharmacy.prescriptionDate")}
                name="date"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={input.date}
                onChange={handleChangePrescriptionField}
                error={!!formErrors.date}
                helperText={formErrors.date}
                sx={{ backgroundColor: "#fff", borderRadius: 2 }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t("pharmacy.time")}
                name="time"
                type="time"
                InputLabelProps={{ shrink: true }}
                value={input.time}
                onChange={handleChangePrescriptionField}
                error={!!formErrors.time}
                helperText={formErrors.time}
                sx={{ backgroundColor: "#fff", borderRadius: 2 }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Autocomplete
                options={ticketOptions}
                getOptionLabel={getTicketOptionLabel}
                inputValue={ticketInputValue}
                onInputChange={handleTicketInputChange}
                value={selectedTicket}
                onChange={handleTicketChange}
                loading={isLoadingTickets}
                renderOption={(props, option) => {
                  const { key, ...restProps } = props;
                  return (
                  <Box key={key} component="li" {...restProps}>
                    <Typography>
                      <strong>{option.ticket_number}</strong>
                      {" — "}
                      {option.patient_name_display || ""}
                      {option.patient_phone_display ? ` (${option.patient_phone_display})` : ""}
                    </Typography>
                  </Box>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("doctor.visitTicket", "Visit ticket")}
                    placeholder={t("doctor.selectTicketPlaceholder", "Search by ticket number or patient name")}
                    variant="outlined"
                    error={!!formErrors.ticket}
                    helperText={formErrors.ticket}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <React.Fragment>
                          {isLoadingTickets ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </React.Fragment>
                      ),
                    }}
                    sx={{ backgroundColor: "#fff", borderRadius: 2 }}
                  />
                )}
              />
            </Grid>
            {selectedTicket && (
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t("pharmacy.patient")}
                  value={selectedTicket.patient_name_display || ""}
                  variant="outlined"
                  InputProps={{ readOnly: true }}
                  sx={{ backgroundColor: "#f5f5f5", borderRadius: 2 }}
                />
              </Grid>
            )}
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                name="doctor"
                variant="outlined"
                label={t("pharmacy.selectDoctor")}
                value={input.doctor}
                onChange={handleChangePrescriptionField}
                error={!!formErrors.doctor}
                helperText={formErrors.doctor}
                sx={{ backgroundColor: "#fff", borderRadius: 2 }}
              >
                {doctors?.map((doctor) => (
                  <MenuItem key={doctor.id} value={doctor.id}>
                    {doctor.first_name} {doctor.last_name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t("pharmacy.duration")}
                name="duration"
                variant="outlined"
                value={input.duration}
                onChange={handleChangePrescriptionField}
                error={!!formErrors.duration}
                helperText={formErrors.duration}
                sx={{ backgroundColor: "#fff", borderRadius: 2 }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="B.P (e.g. 120/80)"
                name="bp"
                variant="outlined"
                value={input.bp}
                onChange={handleChangePrescriptionField}
                sx={{ backgroundColor: "#fff", borderRadius: 2 }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="P.R (Pulse Rate)"
                name="pr"
                variant="outlined"
                value={input.pr}
                onChange={handleChangePrescriptionField}
                sx={{ backgroundColor: "#fff", borderRadius: 2 }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="R.R (Respiratory Rate)"
                name="rr"
                variant="outlined"
                value={input.rr}
                onChange={handleChangePrescriptionField}
                sx={{ backgroundColor: "#fff", borderRadius: 2 }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Diagnosis"
                name="diagnosis"
                variant="outlined"
                multiline
                rows={2}
                value={input.diagnosis}
                onChange={handleChangePrescriptionField}
                sx={{ backgroundColor: "#fff", borderRadius: 2 }}
              />
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Search Medicine Section */}
        <Box mb={4}>
          <Typography variant="subtitle1" fontWeight={500} mb={2}>
            {t("pharmacy.addMedicine")}
          </Typography>
          <Autocomplete
            value={null}
            inputValue={autocompleteInputValue}
            onInputChange={(event, newInputValue) => {
              setAutocompleteInputValue(newInputValue);
            }}
            onChange={(event, value) => {
              handleAddNewItem(value);
              setAutocompleteInputValue("");
            }}
            options={
              medicines
                ? medicines.filter((medicine) =>
                    medicine.quantity > 0 &&
                    medicine.medicine_display
                      .toLowerCase()
                      .startsWith(autocompleteInputValue.toLowerCase())
                  )
                : []
            }
            getOptionLabel={(option) =>
              `${option.medicine_display} (${option.category_display})`
            }
            getOptionKey={(option) => option.id}
            renderOption={(props, option) => {
              const { key, ...restProps } = props;
              return (
              <Box
                key={key}
                component="li"
                {...restProps}
                sx={{
                  padding: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  backgroundColor: "#f9f9f9",
                  borderRadius: 1,
                  "&:hover": {
                    backgroundColor: "#eef2f5",
                  },
                }}
              >
                <Typography
                  variant="body1"
                  fontWeight={600}
                  sx={{ whiteSpace: "nowrap", color: "text.primary" }}
                >
                  {option.medicine_display}
                </Typography>
                <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                <Typography
                  variant="body2"
                  sx={{
                    whiteSpace: "nowrap",
                    color: "text.secondary",
                  }}
                >
                  <strong>{t("pharmacy.category")}:</strong>{" "}
                  {option.category_display}
                </Typography>
                <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                <Typography
                  variant="body2"
                  sx={{
                    whiteSpace: "nowrap",
                    color: "text.secondary",
                  }}
                >
                  <strong>{t("pharmacy.price")}:</strong> {option.sell_price}
                </Typography>
                <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                <Typography
                  variant="body2"
                  sx={{
                    whiteSpace: "nowrap",
                    color: "text.secondary",
                  }}
                >
                  <strong>{t("pharmacy.expiryDate")}:</strong>{" "}
                  {option.expiry_date}
                </Typography>
              </Box>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("pharmacy.searchMedicine")}
                variant="outlined"
                placeholder={t("pharmacy.searchMedicinePlaceholder")}
                inputRef={autocompleteInputRef}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  backgroundColor: "#fff",
                  borderRadius: 2,
                }}
              />
            )}
          />
        </Box>

        {/* Prescription Items Table */}
        <TableContainer component={Paper} elevation={1} sx={{ mb: 4 }}>
          <Table>
            <TableHead sx={{ backgroundColor: "#eef2f5" }}>
              <TableRow>
                <TableCell>{t("pharmacy.medicineName")}</TableCell>
                <TableCell align="center">
                  {t("pharmacy.availableStock")}
                </TableCell>
                <TableCell align="center">{t("pharmacy.quantity")}</TableCell>
                <TableCell align="center" sx={{ fontSize: "0.85rem" }}>
                  {t("pharmacy.unitsPerMeal")}
                </TableCell>
                <TableCell align="center">{t("pharmacy.timesPer24")}</TableCell>
                <TableCell align="center">{t("pharmacy.price")}</TableCell>
                <TableCell align="center">{t("pharmacy.notation")}</TableCell>
                <TableCell align="center">
                  {t("pharmacy.isBeforeMeal")}
                </TableCell>
                <TableCell align="center">{t("pharmacy.totalPrice")}</TableCell>
                <TableCell align="center">{t("pharmacy.action")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody sx={{ bgcolor: "#ccfcfc" }}>
              {input.items.map((item, index) => (
                <TableRow key={index} hover>
                  <TableCell>{item.name}</TableCell>
                  <TableCell align="center">{item.available_stock}</TableCell>
                  <TableCell align="center">
                    <TextField
                      inputRef={(el) => (quantityRefs.current[index] = el)}
                      name="quantity"
                      type="number"
                      size="small"
                      variant="outlined"
                      value={item.quantity}
                      onChange={(e) => handleChangeItemField(index, e)}
                      onFocus={handleZeroReplaceFocus}
                      onClick={handleZeroReplaceClick}
                      onKeyDown={(e) => handleKeyDown(e, index, "quantity")}
                      error={!!formErrors[`quantity_${index}`]}
                      helperText={formErrors[`quantity_${index}`]}
                      inputProps={{ max: item.available_stock }}
                      sx={{ backgroundColor: item.quantity > item.available_stock ? "#fff3f3" : "#fff", borderRadius: 2 }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <TextField
                      inputRef={(el) => (unitsPerMealRefs.current[index] = el)}
                      name="units_per_meal"
                      type="number"
                      size="small"
                      variant="outlined"
                      value={item.units_per_meal}
                      onChange={(e) => handleChangeItemField(index, e)}
                      onFocus={handleZeroReplaceFocus}
                      onClick={handleZeroReplaceClick}
                      onKeyDown={(e) => handleKeyDown(e, index, "units_per_meal")}
                      sx={{ backgroundColor: "#fff", borderRadius: 2 }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <TextField
                      inputRef={(el) => (timesPer24Refs.current[index] = el)}
                      name="times_per_24"
                      type="number"
                      size="small"
                      variant="outlined"
                      value={item.times_per_24 ?? ""}
                      onChange={(e) => handleChangeItemField(index, e)}
                      onFocus={handleZeroReplaceFocus}
                      onClick={handleZeroReplaceClick}
                      onKeyDown={(e) => handleKeyDown(e, index, "times_per_24")}
                      inputProps={{ min: 1, max: 24 }}
                      placeholder="1"
                      sx={{ backgroundColor: "#fff", borderRadius: 2, width: 70 }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <TextField
                      inputRef={(el) => (priceRefs.current[index] = el)}
                      name="sell_price"
                      type="number"
                      size="small"
                      variant="outlined"
                      value={item.sell_price}
                      onChange={(e) => handleChangeItemField(index, e)}
                      sx={{ backgroundColor: "#fff", borderRadius: 2 }}
                      disabled
                    />
                  </TableCell>
                  <TableCell align="center">
                    <TextField
                      inputRef={(el) => (notationRefs.current[index] = el)}
                      name="notation"
                      type="text"
                      size="small"
                      variant="outlined"
                      value={item.notation}
                      onChange={(e) => handleChangeItemField(index, e)}
                      onKeyDown={(e) => handleKeyDown(e, index, "notation")}
                      sx={{ backgroundColor: "#fff", borderRadius: 2 }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <FormControl fullWidth size="small">
                      <Select
                        value={item.is_before}
                        onChange={(e) => handleChangeItemField(index, e)}
                        name="is_before"
                        ref={(el) => (isBeforeRefs.current[index] = el)}
                      >
                        <MenuItem value="before">
                          {t("pharmacy.before")}
                        </MenuItem>
                        <MenuItem value="med">{t("pharmacy.med")}</MenuItem>
                        <MenuItem value="after">{t("pharmacy.after")}</MenuItem>
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell align="center">
                    <Typography fontWeight={600}>{item.total_price}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title={t("pharmacy.removeItem")} arrow>
                      <IconButton
                        color="error"
                        onClick={() => handleRemoveItem(index)}
                      >
                        <RemoveCircleOutline />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Grid container>
          {/* Add Extra Medicines */}
          {extraMedicines?.map((items, index) => {
            return (
              <Grid
                container
                justifyContent={"space-between"}
                gap={2}
                mt={2}
                key={index}
              >
                <TextField
                  name="title"
                  label={t("pharmacy.medicineName")}
                  type="text"
                  size="small"
                  variant="outlined"
                  value={items.title}
                  onChange={(e) =>
                    handleItemChange(index, "title", e.target.value)
                  }
                  sx={{ backgroundColor: "#fff", borderRadius: 2 }}
                />

                <TextField
                  name="quantity"
                  label={t("pharmacy.quantity")}
                  type="text"
                  size="small"
                  variant="outlined"
                  value={items.quantity}
                  onChange={(e) =>
                    handleItemChange(index, "quantity", e.target.value)
                  }
                  sx={{ backgroundColor: "#fff", borderRadius: 2 }}
                />
                <TextField
                  name="dosage"
                  label={t("pharmacy.dosage")}
                  type="text"
                  size="small"
                  variant="outlined"
                  value={items.dosage}
                  onChange={(e) =>
                    handleItemChange(index, "dosage", e.target.value)
                  }
                  sx={{ backgroundColor: "#fff", borderRadius: 2 }}
                />
                <TextField
                  name="notation"
                  label={t("pharmacy.notation")}
                  type="text"
                  size="small"
                  variant="outlined"
                  value={items.notation}
                  onChange={(e) =>
                    handleItemChange(index, "notation", e.target.value)
                  }
                  sx={{ backgroundColor: "#fff", borderRadius: 2 }}
                />

                <TextField
                  name="units_per_meal"
                  label={t("pharmacy.unitsPerMeal")}
                  type="text"
                  size="small"
                  variant="outlined"
                  value={items.units_per_meal}
                  onChange={(e) =>
                    handleItemChange(index, "units_per_meal", e.target.value)
                  }
                  sx={{ backgroundColor: "#fff", borderRadius: 2 }}
                />
                <TextField
                  name="times_per_24"
                  label={t("pharmacy.timesPer24")}
                  type="text"
                  size="small"
                  variant="outlined"
                  value={items.times_per_24 ?? ""}
                  onChange={(e) =>
                    handleItemChange(index, "times_per_24", e.target.value)
                  }
                  placeholder="1"
                  sx={{ backgroundColor: "#fff", borderRadius: 2 }}
                />
                <TextField
                  name="is_before"
                  label={t("pharmacy.isBeforeMeal")}
                  type="text"
                  size="small"
                  variant="outlined"
                  value={items.is_before}
                  onChange={(e) =>
                    handleItemChange(index, "is_before", e.target.value)
                  }
                  sx={{ backgroundColor: "#fff", borderRadius: 2 }}
                />

                <IconButton onClick={() => removeItem(index)} color="error">
                  <RemoveCircleOutline />
                </IconButton>
              </Grid>
            );
          })}
        </Grid>

        <Button variant="outlined" onClick={addItems} sx={{ mt: 1 }}>
          {t("pharmacy.addExtraMedicines", "Add Extra Medicines")}
        </Button>

        {/* Save Button and Total Price Display */}
        <Stack direction="row" justifyContent="flex-end" spacing={2} mt={3}>
          <TextField
            size="small"
            label={t("pharmacy.totalPrice")}
            type="number"
            value={input.total_price}
            sx={{ width: 200, backgroundColor: "#fff", borderRadius: 2 }}
            disabled
          />
          <TextField
            size="small"
            label={t("pharmacy.discount", "Discount")}
            type="number"
            name="discount"
            value={input.discount || 0}
            onChange={handleDiscountChange}
            InputProps={{
              inputProps: { min: 0 },
            }}
            sx={{ width: 200, backgroundColor: "#fff", borderRadius: 2 }}
          />
          <Button
            size="small"
            onClick={handleSave}
            variant="contained"
            disabled={loading}
            sx={{
              fontWeight: 700,
              backgroundColor: "primary.main",
              "&:hover": {
                backgroundColor: "primary.dark",
              },
            }}
          >
            {loading ? t("pharmacy.saving") : t("pharmacy.savePrescription")}
          </Button>
        </Stack>
        </CardContent>
      </Card>

      {printData && (
        <div ref={printRef} style={{ display: "none" }}>
          <PrescriptionPrint data={printData} />
        </div>
      )}
    </>
  );
};

export default AddPrescription;
